import { useRouter } from "next/router";
import type { ParsedUrlQuery } from "querystring";
import { prependLocaleToPath, useLocale } from "./locale";

export const managerAppMarketplacePagePath = "partner/marketplaces";
export const builtfirstCommunitySubdomain = "marketplace";
export interface RouterPushOptions {
  shallow?: boolean;
  scroll?: boolean;
}

const paramsToKeepOnRedirection = [
  "editor-path",
  "editor-preview",
  "template_id",
  "manage_ret_url",
  "editor_feature-internal",
];

function addParamsIfMissing(
  base: string,
  key: string,
  value: ParsedUrlQuery[0],
) {
  const params = new URLSearchParams(base);
  if (value !== undefined && !params.get(key)) {
    params.set(key, String(value || []));
  }
  return params;
}

function addParamToHref<T extends string>(
  href: T,
  key: string,
  value?: ParsedUrlQuery[0],
) {
  const [base, searchAndHash] = href.split("?");
  const [search, hash] = searchAndHash?.split("#") || [];
  const params = addParamsIfMissing(search, key, value);
  const query = params.toString();
  return `${base}${query ? `${base?.includes("?") ? "&" : "?"}${query}` : ""}${
    hash ? `#${hash}` : ""
  }`;
}

function getHrefKeepingRedirectionParams(query: ParsedUrlQuery, href: string) {
  let partialUrl = href;
  paramsToKeepOnRedirection.forEach((param) => {
    const value = query[param];
    partialUrl = addParamToHref(partialUrl, param, value);
  });
  return { href: partialUrl };
}

export function useHrefKeepingRedirectionParams(href: string) {
  const { query } = useRouter();
  return getHrefKeepingRedirectionParams(query, href);
}

export function useNavigateSameTab() {
  const router = useRouter();
  const { locale } = useLocale();
  return {
    push: (url: string, options?: RouterPushOptions) =>
      router.push(
        prependLocaleToPath(
          getHrefKeepingRedirectionParams(router.query, url)?.href,
          locale,
        ),
        undefined,
        {
          ...options,
        },
      ),
    replace: (url: string, options?: RouterPushOptions) =>
      router.replace(
        prependLocaleToPath(
          getHrefKeepingRedirectionParams(router.query, url)?.href,
          locale,
        ),
        undefined,
        {
          ...options,
        },
      ),
  };
}

export function useNavigateNewTabSync() {
  return (url: string) => {
    window.open(url, "_blank");
  };
}

export function useNavigateSameTabExternal() {
  return (url: string) => {
    window.open(url, "_self");
  };
}

export function scrollIntoView({
  element,
  onEnd,
}: {
  element?: Element | null;
  onEnd?: () => void;
}) {
  if (element && onEnd) {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            onEnd();
            intersectionObserver.unobserve(element);
          }, 100);
        }
      },
      {
        threshold: 0.5,
      },
    );
    intersectionObserver.observe(element);
  }
  requestAnimationFrame(() => {
    element?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
    });
  });
}

export class BrowserTab {
  private windowReference: Window | undefined;

  public open() {
    this.windowReference = window.open("about:blank", "_blank") || undefined;
  }

  public setLocation(url: string) {
    if (this.windowReference) {
      this.windowReference.location = url;
    } else {
      console.error("BrowserTab setLocation called without window reference");
    }
  }

  public close() {
    this.windowReference?.close();
  }
}
